import React, { useState, useEffect, ChangeEvent } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
// --------------------------------------------------------------------------------
import { Button } from './Button';
import { Input, InputProps } from './Input';
import { ActionIcon } from './ActionIcon';
import { DropDown, DropDownProps, DropDownBlobProps } from './DropDown';
import { config, Icons } from '../index';

// --------------------------------------------------------------------------------
// 📌  Default style configuration for Accordion
// --------------------------------------------------------------------------------
const styleConfig = {
  backgroundColor: config.white,
  borderRadius: '55px',
  border: `1px solid ${config.silver}`,
};

export interface AppSearchProps {
  /* Search input */
  input?: InputProps;
  /* Search input size */
  type?: 'large' | 'small';
  /* Search input placeholder */
  placeholder?: string;
  /* Search dropdown props */
  dropDown?: DropDownBlobProps[];
  /* Search dropdown config props */
  dropdownConfig?: DropDownProps;
  /* Search input background color */
  backgroundColor?: string;
  /* Search input box shadow */
  boxShadow?: string;
  /* Search input border radius */
  borderRadius?: string;
  /* Search input border */
  border?: string;
  /* Search input padding */
  padding?: string;
  /* Search input margin */
  margin?: string;
  /* Search input additional class props */
  className?: string;
  /* Data input object values */
  data?: DataProps;
  /* Search input onChange handler */
  onChange?: (param?: any) => void;
  /* onClear callBack */
  onClear?: (param?: any) => void;
  /* onSelect callBack */
  onSelected?: (param?: any) => void;
  /* onCatSelect callBack */
  onCatSelected?: (param?: any) => void;
  /* onSearch callBack */
  onSearch?: (param?: any) => void;
}

export interface DataProps {
  search?: string;
}

const StyledWrapper = styled.div<AppSearchProps>`
  position: relative;
  margin: ${(props) => props.margin};
`;

const StyledContainer = styled.div<AppSearchProps>`
  position: relative;
  display: flex;
  align-items: center;

  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius};
  box-shadow: ${(props) => props.boxShadow};
  border: ${(props) => props.border};
  padding: ${(props) => props.padding};
`;

const StyledActionsWrapper = styled.div<AppSearchProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 2px;
  gap: ${config.spacingMedium};
`;

const InputWrapper = styled.div<AppSearchProps>`
  display: flex;
  flex: 1;
  border-right: 1px solid ${config.silver};
  margin: 0 24px 0 85px;
  padding: 0 10px 0 0;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointXXLarge}) {
    border-right: none;
  }

  @media (max-width: ${config.breakPointLarge}) {
    margin: 0 24px;
  }
`;

const SearchBtnWrapper = styled.div<AppSearchProps>`
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointXXLarge}) {
    display: none;
  }
`;

const GlobalStyle = createGlobalStyle`
  .app-search-btn-large, .app-search-btn-small {
    transition: ${config.transitionShort};
    padding: 30px 65px;
  }

  .app-search-btn-small {
    padding: 10px 30px;
  }

  .app-search-container-large, .app-search-container-small {
    transition: ${config.transitionShort};
    height: 100px;
  }

  .app-search-container-small {
    height: 60px;
  }

  @media (max-width: ${config.breakPointLarge}) {
    .app-search-btn-large {
      padding: 20px;
    }
    .app-search-container-large {
      height: 80px;
    }
    .app-search-btn-small {
      padding: 10px;
    }
  }
`;

const SearchIconWrapper = styled.div<AppSearchProps>`
  @media (max-width: ${config.breakPointLarge}) {
    display: none;
  }
`;

export const AppSearch: React.FC<AppSearchProps> = ({
  input,
  placeholder = 'Search product',
  backgroundColor = styleConfig.backgroundColor,
  borderRadius = styleConfig.borderRadius,
  type = 'large',
  boxShadow,
  border = styleConfig.border,
  padding,
  margin,
  dropDown,
  dropdownConfig,
  data,
  // --------------------------------------------------------------------------------
  onChange,
  onClear,
  onSelected,
  onCatSelected,
  onSearch,
  // additional props
  ...props
}) => {
  const [isActive, setActive] = useState(false);
  const [userInput, setInput] = useState<string>('');
  const [category, setCategory] = useState<string>('Category');

  const catSelectHandler = ({ category }: { category: string }) => {
    setCategory(category);
    onCatSelected?.({ category });
  };

  // --------------------------------------------------------------------------------
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    onChange?.(e);
  };

  const handleDropDownSelect = (props: DropDownBlobProps) => {
    setInput(props?.title?.string ?? '');
    // pass selected option as props
    onSelected?.(props);
    // close dropdown if selected
    setActive(!isActive);
  };

  const handleDropDownClear = () => {
    setInput('');
    onClear?.();
  };

  useEffect(() => {
    // flip isActive state if dropDown is active
    if (dropDown) return setActive(!!dropDown?.length);

    setActive(false);
  }, [dropDown]);

  return (
    <StyledWrapper margin={margin}>
      <GlobalStyle />
      <StyledContainer
        backgroundColor={type === 'small' ? config.silver : backgroundColor}
        borderRadius={borderRadius}
        boxShadow={boxShadow}
        border={border}
        padding={padding}
        className={
          type === 'large'
            ? 'app-search-container-large'
            : 'app-search-container-small'
        }
        {...props}
      >
        <div
          style={{
            position: 'absolute',
            left: `calc(${config.spacingXXXXLarge} / 2)`,
          }}
        >
          <SearchIconWrapper>
            <Icons
              icon="search"
              size={40}
              hoverStroke={config.primary}
              onClick={() => {}}
            />
          </SearchIconWrapper>
        </div>
        <InputWrapper>
          <Input
            onChange={handleChange}
            value={userInput}
            // --------------------------------------------------------------------------------
            name="app-search-input"
            height={config.fontXMedium}
            fontSize={config.fontLarge}
            padding={`5px 0`}
            border="none"
            placeholder={placeholder}
            isCounter={false}
            {...input}
          />
        </InputWrapper>

        <StyledActionsWrapper>
          <StyledActionsWrapper>
            <SearchBtnWrapper>
              <Button
                label={{
                  string: category,
                  fontSize: '22px',
                  margin: '0 5vw 0 0',
                }}
                type="transparent"
                onClick={() => console.log('🐞 category api')}
                dropdownOptions={[
                  {
                    title: {
                      string:
                        'Option 1 & Lorem Ipsum is simply dummy text of the printing and typesetting industry...',
                      color: 'green',
                    },
                    onClick: () => catSelectHandler({ category: 'Option 1' }),
                  },
                  {
                    title: { string: 'Option 2' },
                    onClick: () => catSelectHandler({ category: 'Option 2' }),
                  },
                  {
                    title: { string: 'Option 3' },
                    onClick: () => catSelectHandler({ category: 'Option 3' }),
                  },
                  {
                    title: { string: 'Option 4' },
                    onClick: () => catSelectHandler({ category: 'Option 4' }),
                  },
                ]}
                padding="0"
                dropdownConfig={{
                  position: 'right',
                  transform: 'translate3d(30px, 40px, 0)',
                  margin: '0 20px',
                  fontSize: '22px',
                  width: '400px',
                  border: `1px solid ${config.transparent}`,
                }}
                iconConfig={{
                  icon: 'chevron-down',
                  transform: 'rotate(0deg)',
                }}
              />
            </SearchBtnWrapper>

            <Button
              label={{
                string: 'Search',
                fontSize: config.fontLarge,
              }}
              onClick={onSearch}
              className={
                type === 'large'
                  ? 'app-search-btn-large'
                  : 'app-search-btn-small'
              }
            />
          </StyledActionsWrapper>

          {userInput && (
            <ActionIcon
              icon="close"
              size={24}
              backgroundColor={config.primary}
              fill={config.white}
              margin={`0 ${config.spacingMedium}`}
              onClick={handleDropDownClear}
            />
          )}
        </StyledActionsWrapper>
      </StyledContainer>
      <DropDown
        dropDownProps={dropDown}
        isActive={isActive}
        // maxHeight={`calc(${config.spacingXLarge} * 5)`}
        margin={`0 ${config.spacingLarge}`}
        transform="translate3d(0,20px,0)"
        fontSize={config.fontXMedium}
        {...dropdownConfig}
        // add call back to close dropdown
        onClick={(props) => handleDropDownSelect(props)}
        zIndex={88}
      />
    </StyledWrapper>
  );
};
